"use client";

import type { RefObject } from "react";

import { useEffect, useRef } from "react";
import { STEP_HEADING_SELECTOR } from "../constants/step-heading-selector";

/**
 * Moves focus to the new step's heading on every step change after the first
 * settled step, so keyboard and screen reader users land on the question.
 * The first settled step keeps focus where it is, so a deep link landing
 * after hydration doesn't steal focus on page load.
 */
function useStepFocus(
  containerRef: RefObject<HTMLDivElement | null>,
  stepId: string | undefined,
  isSettled: boolean,
) {
  const focusedStepIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isSettled || stepId === undefined) {
      return;
    }

    if (focusedStepIdRef.current === null) {
      focusedStepIdRef.current = stepId;
      return;
    }

    if (focusedStepIdRef.current === stepId) {
      return;
    }

    focusedStepIdRef.current = stepId;
    const container = containerRef.current;
    const heading = Array.from(
      container?.querySelectorAll<HTMLElement>(STEP_HEADING_SELECTOR) ?? [],
    ).find((element) => !element.closest("[inert]"));
    (heading ?? container)?.focus();
  }, [containerRef, isSettled, stepId]);
}

export { useStepFocus };
